import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Search,
  PackageCheck,
  Truck,
  MapPin,
  Clock,
  AlertTriangle,
  CheckCircle2,
  HelpCircle,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input, FieldError } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Seo } from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { trackSchema, type TrackInput } from "@/features/forms/schemas";
import { lookupTracking } from "@/features/tracking/api";
import { StatusTimeline } from "@/features/tracking/StatusTimeline";
import { MapView } from "@/features/tracking/MapView";
import type { ShipmentStatus, TrackingResult } from "@/features/tracking/types";
import { company } from "@/content/site";

const FLOW: { status: ShipmentStatus; label: string }[] = [
  { status: "booked", label: "Booked" },
  { status: "dispatched", label: "Dispatched" },
  { status: "in_transit", label: "In transit" },
  { status: "out_for_delivery", label: "Out for delivery" },
  { status: "delivered", label: "Delivered" },
];

function ProgressBar({ status }: { status: ShipmentStatus }) {
  if (status === "exception") {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 p-4 text-amber-800">
        <AlertTriangle className="h-5 w-5 shrink-0" />
        <span className="text-sm font-medium">
          This shipment has an exception - our team is on it. Contact dispatch for details.
        </span>
      </div>
    );
  }
  const current = FLOW.findIndex((f) => f.status === status);
  return (
    <ol className="min-w-[300px] sm:min-w-0 flex items-center">
      {FLOW.map((f, i) => {
        const done = i <= current;
        const isCurrent = i === current;
        return (
          <li key={f.status} className="flex flex-1 items-center last:flex-none">
            <div className="flex flex-col items-center">
              <span
                className={cn(
                  "flex h-8 w-8 items-center justify-center rounded-full border-2 transition-colors",
                  done ? "border-accent-500 bg-accent-500 text-white" : "border-brand-200 bg-white text-brand-300",
                  isCurrent && "ring-4 ring-accent-500/20",
                )}
              >
                <CheckCircle2 className={cn("h-4 w-4", !done && "hidden")} />
                <span className={cn("text-xs font-bold", done && "hidden")}>{i + 1}</span>
              </span>
              <span className={cn("mt-2 hidden text-center text-xs sm:block", done ? "font-semibold text-brand-900" : "text-brand-400")}>
                {f.label}
              </span>
            </div>
            {i < FLOW.length - 1 && (
              <span className={cn("mx-1 h-0.5 flex-1 rounded-full sm:mx-2", i < current ? "bg-accent-500" : "bg-brand-200")} />
            )}
          </li>
        );
      })}
    </ol>
  );
}

export default function Track() {
  const [params] = useSearchParams();
  const initialRef = params.get("ref") ?? "";
  const [result, setResult] = useState<TrackingResult | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [searched, setSearched] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<TrackInput>({
    resolver: zodResolver(trackSchema),
    defaultValues: { reference: initialRef },
  });

  async function onSubmit({ reference }: TrackInput) {
    setNotFound(false);
    setResult(null);
    setSearched(true);
    try {
      setResult(await lookupTracking(reference));
    } catch {
      setNotFound(true);
    }
  }

  // Auto-search when arriving from the hero tracking input (/track?ref=...)
  useEffect(() => {
    if (initialRef) void handleSubmit(onSubmit)();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Seo
        title={`Track a shipment | ${company.name}`}
        description="Enter your Bull & Bear tracking or PO number to see live status, location, and ETA on your tanker load."
      />

      {/* HERO + SEARCH */}
      <section className="relative overflow-hidden bg-brand-900 text-white">
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.06]"
          style={{ backgroundImage: "repeating-linear-gradient(45deg, #fff 0 1px, transparent 1px 10px)" }}
        />
        <div className="pointer-events-none absolute -right-40 -top-40 h-[440px] w-[440px] rounded-full bg-accent-500/15 blur-3xl" />
        <div className="relative mx-auto max-w-[1200px] px-6 py-16 lg:py-20">
          <div className="mb-3 text-sm font-semibold uppercase tracking-wider text-accent-400">Tracking</div>
          <h1 className="mb-4 text-[clamp(2rem,4vw,3rem)] font-bold leading-[1.05]">Track a shipment</h1>
          <p className="mb-8 max-w-xl text-lg text-brand-200">
            Enter your tracking or PO number for live status, location, and ETA -
            straight from the truck.
          </p>
          <form onSubmit={handleSubmit(onSubmit)} className="max-w-xl" noValidate>
            <div className="flex flex-col gap-2 sm:flex-row">
              <div className="flex-1">
                <Input
                  placeholder="e.g. BB-10482"
                  className="h-12 border-white/20 bg-white text-brand-900"
                  {...register("reference")}
                  aria-invalid={!!errors.reference}
                  aria-label="Tracking or PO number"
                />
              </div>
              <Button type="submit" variant="accent" size="lg" className="w-full sm:w-auto" disabled={isSubmitting}>
                <Search className="h-4 w-4" /> {isSubmitting ? "Searching..." : "Track"}
              </Button>
            </div>
            <FieldError>{errors.reference?.message}</FieldError>
          </form>
        </div>
      </section>

      <section className="mx-auto max-w-[1200px] px-6 py-12 lg:py-16">
        {/* NOT FOUND */}
        {notFound && (
          <Reveal className="rounded-xl border border-amber-200 bg-amber-50 p-6">
            <div className="flex items-start gap-3">
              <AlertTriangle className="mt-0.5 h-6 w-6 shrink-0 text-amber-600" />
              <div>
                <h2 className="font-semibold text-amber-900">We couldn't find that reference</h2>
                <p className="mt-1 text-sm text-amber-800">
                  Double-check the tracking or PO number. Still stuck?{" "}
                  <Link to="/contact" className="font-semibold underline">
                    Contact dispatch
                  </Link>{" "}
                  or call{" "}
                  <a href={`tel:${company.phone.replace(/[^+\d]/g, "")}`} className="font-semibold underline">
                    {company.phone}
                  </a>
                  .
                </p>
              </div>
            </div>
          </Reveal>
        )}

        {/* RESULT */}
        {result && (
          <Reveal className="space-y-8">
            <div className="rounded-xl border border-brand-100 bg-white p-6 shadow-sm lg:p-8">
              <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
                <div>
                  <div className="text-sm text-brand-500">Reference</div>
                  <div className="text-2xl font-bold text-brand-900">#{result.reference}</div>
                </div>
                <Badge variant="accent" className="text-sm capitalize">
                  {result.status.replace(/_/g, " ")}
                </Badge>
              </div>

              <div className="mb-8 overflow-x-auto pb-2">
                <ProgressBar status={result.status} />
              </div>

              <div className="flex flex-wrap gap-x-8 gap-y-3 border-t border-brand-100 pt-6 text-sm">
                <div className="flex min-w-0 flex-wrap items-center gap-2 text-brand-700">
                  <MapPin className="h-4 w-4 text-accent-500" />
                  <span className="font-medium">{result.origin}</span>
                  <span className="text-brand-400">&rarr;</span>
                  <span className="font-medium">{result.destination}</span>
                </div>
                {result.eta && (
                  <div className="flex items-center gap-2 text-brand-700">
                    <Clock className="h-4 w-4 text-accent-500" />
                    ETA <span className="font-medium">{new Date(result.eta).toLocaleString()}</span>
                  </div>
                )}
              </div>
            </div>

            <div className="grid gap-6 md:grid-cols-2">
              <div className="rounded-xl border border-brand-100 bg-white p-6">
                <h3 className="mb-4 font-semibold text-brand-900">Status history</h3>
                <StatusTimeline events={result.events} />
              </div>
              <div className="rounded-xl border border-brand-100 bg-white p-6">
                <h3 className="mb-4 font-semibold text-brand-900">Live location</h3>
                {result.location ? (
                  <MapView {...result.location} />
                ) : (
                  <p className="text-sm text-brand-500">Live location not available for this shipment.</p>
                )}
              </div>
            </div>
          </Reveal>
        )}

        {/* INITIAL STATE - guidance before any search */}
        {!searched && !result && (
          <Reveal className="grid gap-8 lg:grid-cols-[1.2fr_1fr]">
            <div>
              <h2 className="mb-2 text-2xl font-bold text-brand-900">What you'll see</h2>
              <p className="mb-6 max-w-prose text-brand-600">
                Pop in your Bull &amp; Bear reference and we'll pull the load's
                current milestone, route, ETA, and live truck location - the same
                data our dispatch team is looking at.
              </p>
              <ul className="grid gap-4 sm:grid-cols-3">
                {[
                  { icon: PackageCheck, t: "Live status", d: "Booked through delivered" },
                  { icon: Truck, t: "Truck location", d: "GPS position + last update" },
                  { icon: Clock, t: "Real ETA", d: "Based on actual movement" },
                ].map((x) => (
                  <li key={x.t} className="rounded-lg border border-brand-100 p-5">
                    <x.icon className="mb-2 h-6 w-6 text-accent-500" />
                    <div className="font-semibold text-brand-900">{x.t}</div>
                    <div className="text-sm text-brand-500">{x.d}</div>
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-xl border border-dashed border-brand-200 bg-brand-50 p-6">
              <div className="flex items-start gap-3">
                <HelpCircle className="mt-0.5 h-6 w-6 shrink-0 text-brand-400" />
                <div>
                  <h3 className="font-semibold text-brand-900">Don't have a reference?</h3>
                  <p className="mt-1 text-sm text-brand-600">
                    Your tracking or PO number is on your rate confirmation or BOL.
                    If you can't find it, our team can look it up for you.
                  </p>
                  <Link to="/contact" className="mt-3 inline-block text-sm font-semibold text-accent-600 hover:underline">
                    Contact dispatch &rarr;
                  </Link>
                </div>
              </div>
            </div>
          </Reveal>
        )}
      </section>
    </>
  );
}
